/**
 * The query client, and the keys everything reads by.
 *
 * Before this, each screen loaded its own data in a `useEffect` and kept the result in
 * `useState`: eight copies of the same shape, each with its own loading flag, its own
 * error string and its own hand-written refetch after every mutation. That pattern is
 * what the lint rule `react-hooks/set-state-in-effect` was flagging — and it was turned
 * off with a note saying the fix was a query library rather than a restructure. This is
 * that library.
 *
 * The keys live here rather than at each call site so that a mutation can invalidate
 * what it changed without the two drifting apart, which is the failure mode that makes
 * a cache worse than no cache.
 */
import { QueryCache, QueryClient } from '@tanstack/react-query'
import { ApiError } from './api'

/**
 * A rejected request is not retried.
 *
 * A 4xx is an answer: the body is wrong, the caller is not permitted, or the session is
 * gone, and sending it again changes nothing while making the UI slower to explain
 * itself. Server and network failures are worth one more attempt.
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
	if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false
	return failureCount < 2
}

export const queryKeys = {
	session: ['session'] as const,
	authMethods: ['auth-methods'] as const,
	connections: ['connections'] as const,
	deliverySettings: ['delivery-settings'] as const,
	plan: ['plan'] as const,
	billingProfile: ['billing-profile'] as const,
	domains: ['domains'] as const,
	templates: ['templates'] as const,
	template: (id: string) => ['template', id] as const,
	assignments: ['assignments'] as const,
	rules: ['rules'] as const,
	identities: ['identities'] as const,
	coverage: ['coverage'] as const,
	coverageGrowth: (from: string, to: string) => ['coverage', 'growth', from, to] as const,
	sendAsGrants: ['send-as-grants'] as const,
	groupMemberships: ['group-memberships'] as const,
	audit: ['audit'] as const,
	team: ['team'] as const,
	teamMembers: ['team', 'members'] as const,
	teamInvitations: ['team', 'invitations'] as const,
	teamSummary: ['team', 'summary'] as const,
	roles: ['roles'] as const,
	profile: ['profile'] as const,
}

/**
 * Drops every organisation-scoped query when the active organisation changes.
 *
 * The keys carry no tenant, and data stays fresh for a minute. Without this, a switch would
 * keep showing the previous organisation's templates and people until each query refetched.
 * The queries are *reset*, not invalidated: invalidating keeps the old data on screen while
 * the refetch is in flight. The session and the organisation list belong to the account, not
 * the tenant, so they are kept, and the caller reloads the session itself.
 */
export async function resetTenantQueries(queryClient: QueryClient): Promise<void> {
	await queryClient.resetQueries({
		// 'organizations' is ORGANIZATIONS_QUERY_KEY, spelled out because importing it would pull
		// the auth client into this module.
		predicate: (query) => query.queryKey[0] !== queryKeys.session[0] && query.queryKey[0] !== 'organizations',
	})
}

export interface QueryClientOptions {
	/**
	 * Overrides the retry backoff.
	 *
	 * Only tests pass this: the default backoff is seconds, and a suite that waited it out
	 * would be slow enough that someone would delete the assertion instead.
	 */
	retryDelay?: number | undefined
}

export function createQueryClient(options: QueryClientOptions = {}): QueryClient {
	/* A 401 anywhere means the session is gone, and invalidating the session query is what
	 * sends the person back to the sign-in screen instead of leaving every panel showing
	 * "could not load". The handler needs the client that is being constructed here, so it
	 * reaches it through a holder filled in immediately afterwards. */
	const holder: { client?: QueryClient } = {}

	const queryCache = new QueryCache({
		onError: (error) => {
			if (error instanceof ApiError && error.isUnauthenticated) {
				void holder.client?.invalidateQueries({ queryKey: queryKeys.session })
			}
		},
	})

	const client = new QueryClient({
		queryCache,
		defaultOptions: {
			queries: {
				retry: shouldRetry,
				...(options.retryDelay === undefined ? {} : { retryDelay: options.retryDelay }),
				/*
				 * A minute. The console is used by an administrator looking at their own
				 * organisation, and this is a screen they sit on rather than a feed: refetching
				 * on every window focus produced more requests than information.
				 */
				staleTime: 60_000,
				refetchOnWindowFocus: false,
			},
			mutations: {
				// A mutation that fails is reported to the person who asked for it; retrying a
				// write behind their back is how the same signature gets applied twice.
				retry: false,
			},
		},
	})

	holder.client = client
	return client
}
